import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { AdminNavigationComponent } from '../../components/admin-navigation/admin-navigation.component';
import { AdminTopbarComponent } from '../../components/admin-topbar/admin-topbar.component';
import { CustomButtonComponent } from '../../../../shared/components/custom-button/custom-button.component';
import { AdminService } from '../../../../core/api/admin.service';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { ExtractionQAFileData, ExtractionQAStatsGet, QAStatsOverviewDay } from '../../../../core/interfaces/admin-interfaces';
import { IsoToFormattedDateTimePipe } from '../../../../core/pipes/iso-to-formatted-date-time.pipe';
import { ExpandedExtractionQAComponent } from '../../components/expanded-extraction-qa/expanded-extraction-qa.component';
import { QaOverviewChartComponent } from '../../components/qa-overview-chart/qa-overview-chart.component';
import { QaFieldErrorsComponent } from '../../components/qa-field-errors/qa-field-errors.component';

@Component({
  selector: 'app-admin-extraction-qa-page',
  standalone: true,
  imports: [
    CommonModule, TranslocoPipe, ToastComponent, AdminNavigationComponent,
    AdminTopbarComponent, CustomButtonComponent, IsoToFormattedDateTimePipe,
    ExpandedExtractionQAComponent, QaOverviewChartComponent, QaFieldErrorsComponent
  ],
  templateUrl: './admin-extraction-qa-page.component.html',
  styleUrl: './admin-extraction-qa-page.component.css',
  animations: [
    trigger('expandRow', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('500ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class AdminExtractionQAPageComponent implements OnInit {
  constructor(
    private router: Router,
    private adminService: AdminService,
    private apiError: ApiErrorService
  ) { }

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  // Data
  qaStats: ExtractionQAStatsGet[] = [];
  loadedQaStats: ExtractionQAStatsGet[] = [];

  lastLoadedPageNumber: number = 0;
  loadMoreHidden = false;
  statsFetchedCount: number = 0;

  // Overview chart
  overviewData: QAStatsOverviewDay[] = [];
  overviewLoaded: boolean = false;

  // Expanded Row
  expandedRow: number | null = null;
  loadingExpandedRow: boolean = false;
  expandedQAData: ExtractionQAFileData | null = null;

  ngOnInit(): void {
    this.loadOverviewChart();
    this.reloadStats();
  }

  goToDashboard() {
    this.router.navigate(['/dashboard']);
  }

  invoicePeriodType: string = 'mensile';

  loadOverviewChart() {
    this.adminService.getQAStatsOverview(this.invoicePeriodType).subscribe({
      next: (response) => {
        this.overviewData = response.data;
        this.overviewLoaded = true;
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
    });
  }

  loadStatsNewPage() {
    this.adminService.getQAStats(this.lastLoadedPageNumber + 1, 20, this.invoicePeriodType).subscribe({
      next: (response) => {
        if (response) {
          if (response.data.length === 0 || this.statsFetchedCount + response.data.length >= response.totalCount) {
            this.loadMoreHidden = true;
          }
          this.statsFetchedCount += response.data.length;
          this.loadedQaStats = [...this.loadedQaStats, ...response.data];
          this.resetStatsFilter();
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {
        this.lastLoadedPageNumber++;
      }
    });
  }

  resetStatsFilter() {
    this.qaStats = [];
    this.qaStats = this.loadedQaStats;
  }

  reloadStats = () => {
    this.loadedQaStats = [];
    this.qaStats = [];
    this.loadMoreHidden = false;
    this.lastLoadedPageNumber = 0;
    this.statsFetchedCount = 0;
    this.expandedRow = null;
    this.loadStatsNewPage();
  };

  async toggleDetails(index: number): Promise<void> {
    if (this.loadingExpandedRow) return;

    if (this.expandedRow === index) {
      this.expandedRow = null;
      return;
    }

    this.loadingExpandedRow = true;

    try {
      const response = await lastValueFrom(this.adminService.getQAFileData(this.qaStats[index].file_id, this.invoicePeriodType));
      this.expandedQAData = response;
      this.expandedRow = index;
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }

    this.loadingExpandedRow = false;
  }

  getExtractedClass(value: number, other: number): string {
    if (value > other) return 'text-success-500';
    if (value < other) return 'text-error-500';
    return '';
  }

  getAccuracyClass(accuracy: number): string {
    if (accuracy >= 90) return 'text-success-500';
    if (accuracy >= 70) return 'text-warning-500';
    return 'text-error-500';
  }

  getImprovementText(stats: ExtractionQAStatsGet): string {
    const diff = stats.correct_fields_new - stats.correct_fields_old;
    const sign = diff >= 0 ? '+' : '';
    return `${sign}${diff}`;
  }

  getImprovementClass(stats: ExtractionQAStatsGet): string {
    const diff = stats.correct_fields_new - stats.correct_fields_old;
    if (diff > 0) return 'text-success-500';
    if (diff < 0) return 'text-error-500';
    return '';
  }
}
